/**
 * Uchi — SPOON gift auto-add (14 Oct 2026)
 *
 * Rules
 * - SPOON is "on" when the cart carries it (cart.discount_codes) or the visitor arrived via
 *   /discount/SPOON (Shopify's discount_code cookie; the code then applies at checkout).
 * - Eligible subtotal = final_line_price of lines from eligible products, Spoon lines excluded.
 * - On + subtotal >= min + no Spoon in cart  -> add 1 Spoon, tagged _uchi_gift=SPOON (hidden property).
 * - Our tagged Spoon + (code off or subtotal < min) -> remove it, so nobody pays €12 for a Spoon
 *   they never chose.
 * - If the customer removes our Spoon themselves, we don't add it back this session.
 * - Never touches a Spoon the customer added on their own.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const cfg = window.UchiSpoonGift;
const SOURCE = 'uchi-spoon-gift';
const KEY_ADDED = 'uchiSpoonGift:added';
const KEY_OPTOUT = 'uchiSpoonGift:optout';

const store = {
  get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  del(k) { try { sessionStorage.removeItem(k); } catch (e) {} },
};

const codeInCookie = () => {
  const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
  if (!m) return false;
  return decodeURIComponent(m[1]).split(',').some((c) => c.trim().toUpperCase() === cfg.code);
};

const codeOnCart = (cart) =>
  (cart.discount_codes || []).some((d) => String(d.code).toUpperCase() === cfg.code) || codeInCookie();

const isSpoon = (item) => item.product_id === cfg.spoonProductId;
const isOurs = (item) => isSpoon(item) && item.properties && item.properties._uchi_gift === cfg.code;

const eligibleCents = (cart) =>
  cart.items
    .filter((i) => !isSpoon(i) && cfg.eligibleProductIds.includes(i.product_id))
    .reduce((sum, i) => sum + (i.final_line_price || 0), 0);

const json = (url, body) =>
  fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

let busy = false;
let again = false;

async function sync() {
  if (busy) { again = true; return; }
  busy = true;
  try {
    const cart = await json(cfg.routes.cart);
    if (cart.currency && cart.currency !== cfg.currency) return;

    const on = codeOnCart(cart);
    const qualifies = on && eligibleCents(cart) >= cfg.minCents;
    const spoonLines = cart.items.filter(isSpoon);
    const ours = cart.items.find(isOurs);

    let changed = false;

    if (qualifies && spoonLines.length === 0) {
      if (store.get(KEY_ADDED)) {
        // We added it earlier and it's gone while still qualifying: the customer removed it.
        store.set(KEY_OPTOUT, '1');
        store.del(KEY_ADDED);
      } else if (!store.get(KEY_OPTOUT)) {
        await json(cfg.routes.add, {
          items: [{ id: cfg.spoonVariantId, quantity: 1, properties: { _uchi_gift: cfg.code } }],
        });
        store.set(KEY_ADDED, '1');
        changed = true;
      }
    } else if (!qualifies && ours) {
      await json(cfg.routes.change, { id: ours.key, quantity: 0 });
      store.del(KEY_ADDED);
      changed = true;
    } else if (!qualifies) {
      // Cart emptied or code gone: reset, so re-qualifying later isn't mistaken for an opt-out.
      store.del(KEY_ADDED);
    }

    if (changed) {
      const fresh = await json(cfg.routes.cart);
      document.dispatchEvent(
        new CartUpdateEvent(fresh, SOURCE, { source: SOURCE, itemCount: fresh.item_count })
      );
    }
  } catch (e) {
    // Out of stock, network, etc.: fail silently, the cart stays as the customer left it.
  } finally {
    busy = false;
    if (again) { again = false; sync(); }
  }
}

if (cfg) {
  document.addEventListener(ThemeEvents.cartUpdate, (e) => {
    if (e.detail && e.detail.sourceId === SOURCE) return;
    // Let the theme's own request settle first.
    setTimeout(sync, 150);
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => setTimeout(sync, 150));
  sync();
}
